import { useMemo } from 'react'

import {
  useFilters, useModals, useSetState, useShowModal,
} from '@domain-group/react-hooks'
import type { UseFiltersPayload } from '@domain-group/react-hooks'

import { ArtworkDownloadContext, BuildingArtworkModal, BuildingImageModal } from '@domain-group/artwork-builder'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateCreditNoteAndDeleteOrderModal from '@modals/CreateCreditNoteAndDeleteOrderModal'
import CreateOrEditCampaignItemModal from '@modals/CreateOrEditCampaignItemModal'
import CreateOrEditOrderItemTaskModal from '@modals/CreateOrEditOrderItemTaskModal'
import EditOrderTotalModal from '@modals/EditOrderTotalModal'
import GenerateCampaignScheduleModal from '@modals/GenerateCampaignScheduleModal'
import ManageActivityModal from '@modals/ManageActivityModal'
import ManageOrderItemModal from '@modals/ManageOrderItemModal'
import ManageOrderItemTasksModal from '@modals/ManageOrderItemTasksModal'
import NewOrderModal from '@modals/NewOrderModal'
import UnapproveOrderConfirmationModal from '@modals/UnapproveOrderConfirmationModal'

import type {
  ActivityModel, CampaignModel, CampaignItemModel, OrderItemModel, OrderItemTaskModel, OrderModel,
} from '@models/types'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showCreateCreditNoteAndDeleteOrderModal: () => void,
    showCreateOrEditCampaignItemModal: () => void,
    showCreateOrEditOrderItemTaskModal: () => void,
    showEditOrderTotalModal: () => void,
    showGenerateCampaignScheduleModal: () => void,
    showManageOrderItemModal: () => void,
    showManageOrderItemTasksModal: () => void,
    showNewOrderModal: () => void,
    showUnapproveOrderConfirmationModal: () => void,
  },
}

const defaultState = {
  showCreateCreditNoteAndDeleteOrderModal: false,
  showCreateOrEditCampaignItemModal: false,
  showCreateOrEditOrderItemTaskModal: false,
  showEditOrderTotalModal: false,
  showGenerateCampaignScheduleModal: false,
  showManageActivityModal: false,
  showManageOrderItemModal: false,
  showManageOrderItemTasksModal: false,
  showNewOrderModal: false,
  showUnapproveOrderConfirmationModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateCreditNoteAndDeleteOrderModal: {
      closeModal: () => setState({ showCreateCreditNoteAndDeleteOrderModal: false }),
    },
    CreateOrEditCampaignItemModal: {
      closeModal: () => setState({ showCreateOrEditCampaignItemModal: false }),
      createCampaignItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCampaignItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditOrderItemTaskModal: {
      closeModal: () => setState({ showCreateOrEditOrderItemTaskModal: false }),
      completeOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      createOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditOrderTotalModal: {
      closeModal: () => setState({ showEditOrderTotalModal: false }),
      updateOrder: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    GenerateCampaignScheduleModal: {
      closeModal: () => setState({ showGenerateCampaignScheduleModal: false }),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    ManageOrderItemModal: {
      closeModal: () => setState({ showManageOrderItemModal: false }),
      duplicateOrderItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      reorderItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateOrderItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageOrderItemTasksModal: {
      closeModal: () => setState({ showManageOrderItemTasksModal: false }),
    },
    NewOrderModal: {
      addItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showNewOrderModal: false }),
    },
    UnapproveOrderConfirmationModal: {
      closeModal: () => setState({ showUnapproveOrderConfirmationModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

type ApprovedOrdersProps = {
  campaign: CampaignModel,
}

const ApprovedOrders = (props: ApprovedOrdersProps) => {
  const { campaign } = props

  const [state, setState] = useSetState(defaultState)
  const {
    showCreateCreditNoteAndDeleteOrderModal,
    showCreateOrEditCampaignItemModal,
    showCreateOrEditOrderItemTaskModal,
    showEditOrderTotalModal,
    showGenerateCampaignScheduleModal,
    showManageActivityModal,
    showManageOrderItemModal,
    showManageOrderItemTasksModal,
    showNewOrderModal,
    showUnapproveOrderConfirmationModal,
  } = state

  const filtersPayload = useFilters({ persistFilters: true })
  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData }, topModalKey } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateCreditNoteAndDeleteOrderModal: (payload: { campaignItem?: CampaignItemModel }) => {
        setModalData('CreateCreditNoteAndDeleteOrderModal', payload)
        setState({ showCreateCreditNoteAndDeleteOrderModal: true })
      },
      showCreateOrEditCampaignItemModal: (payload: { campaignItem: CampaignItemModel }) => {
        setModalData('CreateOrEditCampaignItemModal', payload)
        setState({ showCreateOrEditCampaignItemModal: true })
      },
      showCreateOrEditOrderItemTaskModal: (payload: { orderItemTask: OrderItemTaskModel }) => {
        setModalData('CreateOrEditOrderItemTaskModal', payload)
        setState({ showCreateOrEditOrderItemTaskModal: true })
      },
      showEditOrderTotalModal: (payload: { order: OrderModel }) => {
        setModalData('EditOrderTotalModal', payload)
        setState({ showEditOrderTotalModal: true })
      },
      showGenerateCampaignScheduleModal: () => {
        setState({ showGenerateCampaignScheduleModal: true })
      },
      showManageActivityModal: (payload: { orderItem: ActivityModel }) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showManageOrderItemModal: (payload: { orderItem: OrderItemModel }) => {
        setModalData('ManageOrderItemModal', payload)
        setState({ showManageOrderItemModal: true })
      },
      showManageOrderItemTasksModal: (payload: { orderItem: OrderItemModel }) => {
        setModalData('ManageOrderItemTasksModal', payload)
        setState({ showManageOrderItemTasksModal: true })
      },
      showNewOrderModal: (payload: { agencyId: number, campaignId: number }) => {
        setModalData('NewOrderModal', payload)
        setState({ showNewOrderModal: true })
      },
      showUnapproveOrderConfirmationModal: (payload: { agencyId: number, campaignId: number }) => {
        setModalData('UnapproveOrderConfirmationModal', payload)
        setState({ showUnapproveOrderConfirmationModal: true })
      },
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt, topModalKey]) // modalContext dependency required if layering modals

  useShowModal({
    modalKey: 'ManageOrderItemModal',
    options: {
      // @ts-expect-error TS2322
      callbacks: pageContext.callbacks,
    },
  })

  useShowModal({
    modalKey: 'CreateOrEditOrderItemTaskModal',
    options: {
      // @ts-expect-error TS2322
      callbacks: pageContext.callbacks,
    },
  })

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        {/*
         // @ts-expect-error TS2322 */}
        <PageContent campaign={campaign} pageFilters={pageFilters} />

        <BuildingArtworkModal context={ArtworkDownloadContext} />

        <BuildingImageModal context={ArtworkDownloadContext} />

        <CreateCreditNoteAndDeleteOrderModal
          callbacks={callbacks('CreateCreditNoteAndDeleteOrderModal', setState)}
          showModal={showCreateCreditNoteAndDeleteOrderModal}
        />

        <CreateOrEditCampaignItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignItemModal', setState)}
          showModal={showCreateOrEditCampaignItemModal}
        />

        <CreateOrEditOrderItemTaskModal
          allowedTaskTypes={['clean', 'move', 'other', 'remove', 'repair']}
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditOrderItemTaskModal', setState)}
          showModal={showCreateOrEditOrderItemTaskModal}
        />

        <EditOrderTotalModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditOrderTotalModal', setState)}
          showModal={showEditOrderTotalModal}
          requestOptions={{
            ...defaultRequestOptions.order,
          }}
        />

        <GenerateCampaignScheduleModal
          callbacks={callbacks('GenerateCampaignScheduleModal', setState)}
          campaign={campaign}
          showModal={showGenerateCampaignScheduleModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />

        <ManageOrderItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageOrderItemModal', setState)}
          showModal={showManageOrderItemModal}
        />

        <ManageOrderItemTasksModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageOrderItemTasksModal', setState)}
          showModal={showManageOrderItemTasksModal}
        />

        <NewOrderModal
          callbacks={callbacks('NewOrderModal', setState)}
          showModal={showNewOrderModal}
        />

        <UnapproveOrderConfirmationModal
          callbacks={callbacks('UnapproveOrderConfirmationModal', setState)}
          showModal={showUnapproveOrderConfirmationModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default ApprovedOrders
